import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IFetchStandards,
  IStandardsFetchParam,
} from "@/search/NewSearchPageMVP/types/IFetchStandards";
import {
  IStandard,
  IStandardResponse,
} from "@/search/NewSearchPageMVP/types/IStandard";
import { IUseLazyStandardsFetchWithParams } from "@/search/NewSearchPageMVP/types/IUseLazyStandardsFetchWithParams";
import { useLazyStandardsFetch } from "@/search/useLazyStandardsFetch";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { checkForEqualContentIds } from "../StandardsSearchProvider.utils";
import { useStandardsQuery } from "./useStandardsQuery";

type lazyStandardsFetchReturnType = [
  (params: IUseLazyStandardsFetchWithParams) => void,
  {
    data: IStandardResponse | undefined;
    loading: boolean;
    error: Error;
  },
];

const initialFetchValue = {
  results: [],
};
const initialFetchValueWithCount = {
  ...initialFetchValue,
  total_count: 0,
};
const defaultFetchStandards: IFetchStandards = {
  shouldFetch: false,
  fetchParams: {},
};

export const useStandardsFetch = (
  currentPage: number,
  currentNeedle: string,
) => {
  const { fetchParamsForQuery, standardsInQuery, searchParamsObj } =
    useStandardsQuery();

  const { data: user } = useUser();
  const { isStudent } = getUserProperties(user, { shouldUseNewUserApi: false });
  const canSeeStandardsFilter = !isStudent;
  const classroomGradeLevels = user?.classroom_grade_levels || [];

  const [fetchStandards, setFetchStandards] = useState<IFetchStandards>(
    defaultFetchStandards,
  );
  const [allStandardsModalResults, setAllStandardsModalResults] = useState<
    IStandard[]
  >([]);
  const firstPageStandards = useRef<IStandard[]>([]);
  const lastUpdatedModalResults = useRef<IStandard[]>([]);
  const [selectedStandards, setSelectedStandards] = useState<IStandard[]>([]);
  const temporarySelectedFilters = useRef<IStandard[]>([]);

  const lastUpdatedPage = useRef<number>(0);

  const standardsLazyFetchParams = useMemo(
    () => ({
      skip: !canSeeStandardsFilter,
      classroomGradeLevels,
    }),
    [canSeeStandardsFilter, classroomGradeLevels],
  );

  const [
    fetchStandardsModalResults,
    {
      data: {
        results: standardsModalResults,
        total_count: totalStandardsModalResults,
      } = initialFetchValueWithCount,
      loading: isLoadingStandardsModalResults,
    },
  ] = useLazyStandardsFetch(
    standardsLazyFetchParams,
  ) as lazyStandardsFetchReturnType;

  const [
    fetchTop5StandardsResults,
    { data: { results: top5StandardsResults } = initialFetchValue },
  ] = useLazyStandardsFetch(
    standardsLazyFetchParams,
  ) as lazyStandardsFetchReturnType;

  const [
    fetchSelectedStandardsResults,
    { data: { results: selectedStandardsResults } = initialFetchValue },
  ] = useLazyStandardsFetch(
    standardsLazyFetchParams,
  ) as lazyStandardsFetchReturnType;

  const fetchFilteredStandards = useCallback(
    (params?: IStandardsFetchParam) => {
      setFetchStandards({
        shouldFetch: true,
        fetchParams: {
          ...searchParamsObj,
          ...(params || {}),
        },
      });
    },
    [searchParamsObj],
  );

  const fetchSelectedStandards = useCallback(() => {
    if (
      selectedStandards.length !== standardsInQuery.length &&
      selectedStandardsResults &&
      ((!checkForEqualContentIds(selectedStandardsResults, standardsInQuery) &&
        selectedStandards.length === selectedStandardsResults.length) ||
        selectedStandardsResults.length !== standardsInQuery.length) &&
      fetchParamsForQuery
    ) {
      fetchSelectedStandardsResults(fetchParamsForQuery);
    }
  }, [
    standardsInQuery,
    fetchParamsForQuery,
    selectedStandardsResults,
    selectedStandards,
  ]);

  const resetStandardsList = () => {
    setAllStandardsModalResults(firstPageStandards.current);
  };

  const updateSelectedStandardsOnChange = useCallback(
    (
      isChecked: boolean,
      standard: IStandard,
      updateSelectedStandards: boolean = false,
    ) => {
      const currentSelectedStandards =
        temporarySelectedFilters.current.length === 0
          ? [...selectedStandards]
          : [...temporarySelectedFilters.current];
      temporarySelectedFilters.current = currentSelectedStandards;

      temporarySelectedFilters.current = isChecked
        ? [...currentSelectedStandards, standard]
        : currentSelectedStandards.filter(
            (std) => std.content_id !== standard.content_id,
          );

      if (updateSelectedStandards) {
        setSelectedStandards(temporarySelectedFilters.current);
        temporarySelectedFilters.current = [];
      }
    },
    [selectedStandards],
  );

  const getTemporarySelectedFilters = () => {
    return temporarySelectedFilters.current;
  };

  useEffect(() => {
    // We only need the first five standards to display in the filter section.
    // A subsequent call to get more standards will be made if user clicks into modal.
    fetchTop5StandardsResults({ page_size: 5, ...searchParamsObj });
  }, [searchParamsObj]);

  useEffect(() => {
    if (
      standardsInQuery.length !== selectedStandards.length ||
      !checkForEqualContentIds(selectedStandards, standardsInQuery)
    ) {
      fetchSelectedStandards();
    }

    if (
      selectedStandardsResults?.length &&
      selectedStandardsResults.length === standardsInQuery.length
    ) {
      setSelectedStandards(selectedStandardsResults);
      temporarySelectedFilters.current = [];
    }
  }, [selectedStandardsResults, standardsInQuery, selectedStandards]);

  useEffect(() => {
    const { shouldFetch, fetchParams } = fetchStandards;
    if (shouldFetch) {
      fetchStandardsModalResults({ ...fetchParams });
      setFetchStandards(defaultFetchStandards);
    }
  }, [fetchStandards]);

  useEffect(() => {
    if (
      isLoadingStandardsModalResults ||
      !standardsModalResults ||
      checkForEqualContentIds(
        standardsModalResults,
        allStandardsModalResults,
      ) ||
      currentPage === lastUpdatedPage.current ||
      (checkForEqualContentIds(
        standardsModalResults,
        lastUpdatedModalResults.current,
      ) &&
        currentNeedle === "")
    ) {
      return;
    }

    if (firstPageStandards.current.length === 0) {
      setAllStandardsModalResults([...standardsModalResults]);
      firstPageStandards.current = [...standardsModalResults];
    } else {
      setAllStandardsModalResults(
        currentPage === 1
          ? [...standardsModalResults]
          : [...allStandardsModalResults, ...standardsModalResults],
      );
      lastUpdatedModalResults.current = standardsModalResults;
      if (currentPage > 1) {
        lastUpdatedPage.current = currentPage;
      }
    }

    if (currentPage === 1 && lastUpdatedPage.current > 1) {
      lastUpdatedPage.current = 0;
    }
  }, [
    standardsModalResults,
    currentPage,
    isLoadingStandardsModalResults,
    allStandardsModalResults,
    currentNeedle,
  ]);

  return {
    allStandardsModalResults,
    selectedStandards,
    updateSelectedStandardsOnChange,
    top5Standards: top5StandardsResults,
    totalStandardsResults: totalStandardsModalResults,
    isLoadingStandardsResults: isLoadingStandardsModalResults,
    fetchFilteredStandards,
    standardsInQuery,
    searchParamsObj,
    resetStandardsList,
    setSelectedStandards,
    getTemporarySelectedFilters,
  };
};
